import { CardBody } from 'components';
import { usePurchaseSummary } from '../hooks/PurchaseSummary.hook';
import { Coupon, Price, Product } from '../types/Add-subscription.types';
import './PurchaseSummary.css';

interface PurchaseSummaryProps {
  products: Product[];
  period: string;
  selectedAddons: Record<string, boolean>;
  counts: Record<string, number>;
  formatCurrency: (value: number, currency: string) => string;
  calculatePrice: (price: Price) => number;
  subtotal: number;
  appliedCoupon: Coupon | null;
}

export const PurchaseSummary = ({
  products,
  period,
  selectedAddons,
  counts,
  formatCurrency,
  calculatePrice,
  subtotal,
  appliedCoupon,
}: PurchaseSummaryProps) => {
  const { categories, discount } = usePurchaseSummary({
    products,
    counts,
    appliedCoupon,
  });

  return (
    <CardBody>
      <div className="summary__information__container">
        <h2 className="summary__information__title">Resumen de la compra</h2>
        <div className="summary__information__content_buy_plan">
          {categories.map(({ title, type }) => {
            const categoryProducts = products.flatMap((product) =>
              product.prices
                .filter(
                  (price) =>
                    price.periocity_id.name === period &&
                    (type.includes('Add-on')
                      ? product.is_addon &&
                        selectedAddons[price.id] &&
                        products.some(
                          (p) =>
                            !p.is_addon &&
                            p.prices.some(
                              (pPrice) =>
                                pPrice.product_type_id.name ===
                                  price.product_type_id.name &&
                                counts[pPrice.id] > 0 &&
                                pPrice.periocity_id.name === period
                            )
                        )
                      : type.includes(price.product_type_id.name) &&
                        counts[price.id] > 0)
                )
                .map((price) => ({ ...price, productTitle: product.title }))
            );

            const categoryTotal = categoryProducts.reduce((total, price) => {
              if (type.includes('Add-on')) {
                const relatedProductCount = products
                  .filter((p) => !p.is_addon)
                  .reduce((count, p) => {
                    return (
                      count +
                      p.prices.reduce((pCount, pPrice) => {
                        if (
                          pPrice.product_type_id.name ===
                            price.product_type_id.name &&
                          pPrice.periocity_id.name === period
                        ) {
                          return pCount + (counts[pPrice.id] || 0);
                        }
                        return pCount;
                      }, 0)
                    );
                  }, 0);

                if (relatedProductCount < price.max_additional) {
                  return total + price.price * relatedProductCount;
                } else {
                  return total + price.additional_price * relatedProductCount;
                }
              }
              return total + calculatePrice(price);
            }, 0);

            if (categoryProducts.length === 0) return null;

            return (
              <div key={title} className="summary__information__category">
                <div className="summary__information__subtotal">
                  <span className="summary__information__subtotal-category">
                    {title.toLowerCase() === 'addons'
                      ? 'Beneficios adicionales'
                      : title}
                  </span>
                  <span className="summary__information__subtotal-number">
                    {formatCurrency(categoryTotal, 'USD')}/
                    {period.toLowerCase()}
                  </span>
                </div>
                <div className="summary__information__product-list">
                  {categoryProducts.map((price) => {
                    const quantity = type.includes('Add-on')
                      ? products
                          .filter((p) => !p.is_addon)
                          .reduce((count, p) => {
                            return (
                              count +
                              p.prices.reduce((pCount, pPrice) => {
                                if (
                                  pPrice.product_type_id.name ===
                                    price.product_type_id.name &&
                                  pPrice.periocity_id.name === period
                                ) {
                                  return pCount + (counts[pPrice.id] || 0);
                                }
                                return pCount;
                              }, 0)
                            );
                          }, 0)
                      : counts[price.id];
                    return (
                      <div
                        key={price.id}
                        className="summary__information__product-item"
                      >
                        <span>
                          {price.alias_name} ({quantity})
                          {price.isRepricingNew && (
                            <span className="repricing-new-pill">Nuevo</span>
                          )}
                        </span>
                        <span>
                          {formatCurrency(
                            type.includes('Add-on')
                              ? quantity < price.max_additional
                                ? price.price * quantity
                                : price.additional_price * quantity
                              : calculatePrice(price),
                            price.currency.acronym
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="summary__information__total-summary">
            <div className="summary__information__subtotal">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal, 'USD')}</span>
            </div>
            {appliedCoupon &&
              appliedCoupon.price.some((p) => counts[p.id] > 0) && (
                <div className="summary__information__discount">
                  <span>Descuento ({appliedCoupon.name})</span>
                  <span className="coupon-purchase-summary">
                    - {formatCurrency(discount, 'USD')}
                  </span>
                </div>
              )}
            <div className="summary__information__total">
              <span>Monto Total</span>
              <span>{formatCurrency(subtotal - discount, 'USD')}</span>
            </div>
          </div>
        </div>
      </div>
    </CardBody>
  );
};
